import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

import Header from "../../components/Header";
import PaiSidebar from "../../components/PaiSidebar";
import TheoryBlock from "./TheoryBlock";
import Task from "../task/Task";
import CodeEditor from "../task/CodeEditor";
import NextTopicButton from "./NextTopic";
import MoreTasks from "./MoreTasks";
import TaskContextInTheory from "./TaskContext";
import fetchProtectedData from "../auth/ProtectDataHeader";
import withTokenRefresh from "../auth/RefreshToken";

import ForLoopDiagram from "../../components/theories/ForLoopDiagram";
import DataTypeDemo   from "../../components/theories/DataTypeDemo";
import ArithmeticOperatorsDemo from "../../components/theories/ArithmeticOperatorsDemo";
import ConditionalFlowDiagram from "../../components/theories/ConditionalFlowDiagram";
import FunctionExamplesDemo from "../../components/theories/FunctionExamplesDemo";
import CollectionsDemo from "../../components/theories/CollectionsDemo";
import ErrorHandlingDemo from "../../components/theories/ErrorHandlingDemo";
import FunctionMethodDemo from "../../components/theories/FunctionMetodsDemo";
import StructInterfaceDemo from "../../components/theories/StructExamplesDemo";
import ArraysSlicesDemo from "../../components/theories/ArraySlicesDemo"; 
import MapsDemo from "../../components/theories/MapsDemo";
import GoroutinesParallelismDemo from "../../components/theories/GoroutinesDemo";

import "./theory.css";

ReactGA.send({
  hitType: "pageview",
  page: "/theory",
  title: "Просмотрели страницу с теорией",
});

const interactiveMap = {
  ForLoopDiagram,
  DataTypeDemo,
  ArithmeticOperatorsDemo,
  ConditionalFlowDiagram,
  FunctionExamplesDemo,
  CollectionsDemo,
  ErrorHandlingDemo,
  FunctionMethodDemo,
  StructInterfaceDemo,
  ArraysSlicesDemo,
  MapsDemo,
  GoroutinesParallelismDemo,
};

function Theory({ disableSidebar = false, disableTasks = false }) {
  const [theoryData, setTheoryData] = useState(null);
  const [combinedTheory, setCombinedTheory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [nextTopicLoading, setNextTopicLoading] = useState(false);
  const [nextTopicError, setNextTopicError]     = useState(null);
  const [user_id, setUserId]   = useState(null);
  const [codes, setCodes]      = useState(() => Array(100).fill(`# Пиши свой код сюда`));
  const [conversation, setConversation] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const selectedTopic  = params.get("topic") || "Введение";
  const selectedCourse = params.get("course_name") || "basic-python";

  function interleaveLists(listDescription, listTasks) {
    const result = [];
    const maxLength = Math.max(listDescription.length, listTasks.length);
    for (let i = 0; i < maxLength; i++) {
      if (i < listDescription.length)
        result.push({ value: listDescription[i], type: "description" });
      if (i < listTasks.length)
        result.push({ value: listTasks[i], type: "task" });
    }
    return result;
  }

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const protectedData = await withTokenRefresh(fetchProtectedData);
        if (!protectedData) {
          navigate("/login");
          return;
        }
        setUserId(protectedData);

        const resp = await fetch(
          `api/theories/by-topic/${selectedTopic}?user_id=${protectedData}&course_name=${selectedCourse}`,
          { method: "GET", headers: { Accept: "application/json" } }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const data = await resp.json();
        setTheoryData(data);
        setCombinedTheory(interleaveLists(data.description, data.tasks));

        // check subscription
        const subR = await fetch(`/api/subscription/?user_id=${protectedData}`, {
          method: "GET", headers: { Accept: "application/json" }
        });
        const subD = await subR.json();
        if (subD.status === "expired") {
          navigate("/courses");
        }
      } catch (e) {
        setError(e.message);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedTopic, selectedCourse, navigate]);

  useEffect(() => window.scrollTo(0, 0), [location]);

  const fetchNextTopic = async () => {
    setNextTopicLoading(true);
    try {
      const resp = await fetch(
        `/api/theories/next/${selectedTopic}?user_id=${user_id}&course_name=${selectedCourse}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const { topic: nextTopic } = await resp.json();
      navigate(`/theory?topic=${nextTopic}&course_name=${selectedCourse}`);
    } catch (e) {
      setNextTopicError(e.message);
    } finally {
      setNextTopicLoading(false);
    }
  };

  const handleCodeChange = (idx, val) => {
    setCodes(src => {
      const arr = [...src];
      arr[idx] = val;
      return arr;
    });
  };

  return (
    <div className="app">
      <Header />
      <div className={`theory-wrapper ${isSidebarOpen ? "shifted" : ""}`}>
        <main className="theory-page-container">
          {loading  && <p>Загрузка...</p>}
          {error    && <p className="error">Ошибка: {error}</p>}
          {theoryData && combinedTheory.map((item, idx) => {
            const { value, type } = item;

            if (disableTasks && type === "task") {
              return null;
            }

            if (type === "description") {
              const re = /^!interactive:([A-Za-z0-9_]+)$/m;
              const match = value.match(re);

              if (match) {
                const name      = match[1];
                const Component = interactiveMap[name];
                if (Component) {
                  const [before, , after] = value.split(re);
                  return (
                    <React.Fragment key={idx}>
                      {before.trim() && <TheoryBlock description={before.trim()} />}
                      <Component />
                      {after.trim()  && <TheoryBlock description={after.trim()} />}
                    </React.Fragment>
                  );
                }
              }

              return <TheoryBlock key={idx} description={value} />;
            }

            return (
              <section className="task-section" key={idx}>
                <Task
                  user_id={user_id}
                  selectedTopic={selectedTopic}
                  taskData={value}
                  loading={loading}
                  error={error}
                  hideThemeSelector
                  hideTopicName
                  showComplexityButtons={false}
                  course_name={selectedCourse}
                />
                <CodeEditor
                  taskData={value}
                  code={codes[idx]}
                  setCode={newVal => handleCodeChange(idx, newVal)}
                  course_name={selectedCourse}
                  user_id={user_id}
                />
                <TaskContextInTheory
                  user_id={user_id}
                  theoryData={theoryData}
                  task_number={value.task_number}
                  code={codes[idx]}
                  course_name={selectedCourse}
                />
              </section>
            );
          })}

          {!disableTasks && (
            <section className="next-topic-container">
              <MoreTasks currentTopic={selectedTopic} course_name={selectedCourse} />
              {!theoryData.is_last_in_course && (
                <NextTopicButton
                onNextTopic={fetchNextTopic}
                loading={nextTopicLoading}
                error={nextTopicError}
                />

              )}
             </section>
          )}

          {!disableSidebar && theoryData && (
            <PaiSidebar
              user_id={user_id}
              topic={theoryData.topic}
              is_task={false}
              conversation={conversation}
              setConversation={setConversation}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              course_name={selectedCourse}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default Theory;
